import {
  Alert,
  Button,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  message,
} from "antd";
import { QRCodeCanvas } from "qrcode.react";
import mowi from "../../generateQR/mowi.png";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { createAdminQr, updateAdminQr } from "../../../services/AdminQrApi";
import {
  selectCurrentAdminId,
  selectCurrentAdminQrOptionalAddressAllowed,
} from "../../../utils/helpers/selectorHelper";
import { AdminQrDTO } from "../../../shared/dtos/adminQrDTO";
import LabelAndInput from "../../common/labelAndInput";
import DatePicker from "../../common/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import ComponentPlacesAutocomplete from "../../map/PlaceAutocomplete";
import { RootState } from "../../../app/store";
import PatchAdminQrDTO from "../../../shared/dtos/PatchAdminQrDTO";
import { setCountry, setOrigin } from "../../../features/map/mapSlice";
import { COUNTRIES } from "../../../utils/constants/countries";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";

const qrUrlValue = process.env.REACT_APP_AIRPORT_QR_CODE_URL;

const expiration = dayjs().add(30, "day").toDate();

const defaultQR: AdminQrDTO = {
  bgColor: "#FFFFFF",
  errorLevel: "M",
  fgColor: "#000000",
  imgQrSize: 32,
  qrSize: 256,
  expiration,
  fee: 10,
};

type AirportQRFormProps = {
  airportQr: AdminQrDTO | null;
  update?: (shouldFetchData: boolean) => void;
};

const AirportQRForm = ({ airportQr, update }: AirportQRFormProps) => {
  const dispatch = useAppDispatch();
  const [qr, setQr] = useState<AdminQrDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [addressInputKey, setAddressInputKey] = useState(0);
  const { destinations, country } = useAppSelector(
    (state: RootState) => state?.map,
  );
  const optionalAddressAllowed = selectCurrentAdminQrOptionalAddressAllowed();

  const onChangeErrorLevel = (value: string | null) => {
    if (value)
      setQr((prev) => {
        return {
          ...prev,
          errorLevel: value,
        };
      });
  };

  const onChangeSize = (size: number | null) => {
    if (size) {
      const imgQrSize = (15 * size) / 100;
      setQr((prev) => {
        return {
          ...prev,
          qrSize: size,
          imgQrSize,
        };
      });
    }
  };

  const onChangeColor = (e: ChangeEvent<HTMLInputElement>, name: string) => {
    const color = e.target.value;
    if (color && name)
      setQr((prev) => {
        return {
          ...prev,
          [name]: color,
        };
      });
  };

  const onChangeExpiration = (date: Dayjs | null) => {
    if (date)
      setQr((prev) => {
        return {
          ...prev,
          expiration: date.toDate(),
        };
      });
  };

  const downloadQRCode = () => {
    const canvas = document.querySelector("#qr-code") as HTMLCanvasElement;
    if (!canvas) throw new Error("<canvas> not found in the DOM");

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = "QR code.png";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  useEffect(() => {
    const getQr = async () => {
      setLoading(true);
      setQr(airportQr);
      dispatch(setCountry(COUNTRIES[1]));
      setLoading(false);
    };

    getQr();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airportQr]);

  const upsertQr = async (isUpdate = false) => {
    if (!optionalAddressAllowed && !destinations?.pickUp) {
      message.error("Inserte una dirección!");
      return;
    }
    try {
      setLoading(true);
      if (isUpdate) {
        const patchAdminQr: PatchAdminQrDTO = new PatchAdminQrDTO();

        if (qr) {
          patchAdminQr.address = destinations?.pickUp?.originAddress;
          patchAdminQr.longitude = destinations?.pickUp?.originLongitude;
          patchAdminQr.latitude = destinations?.pickUp?.originLatitude;
          patchAdminQr.fee = qr.fee;
          patchAdminQr.countryId = country.value;
        }

        await updateAdminQr(patchAdminQr, qr?.id);
        message.success("QR actualizado satisfactoriamente.");
      } else {
        if (qr) {
          qr.address = destinations?.pickUp?.originAddress;
          qr.longitude = destinations?.pickUp?.originLongitude;
          qr.latitude = destinations?.pickUp?.originLatitude;
          qr.isAirportQr = true;
          qr.countryId = country.value;
        }

        await createAdminQr({
          ...qr,
          adminId: selectCurrentAdminId(),
        });
        message.success("QR generado satisfactoriamente.");
      }
    } catch (error: any) {
      if (error.response.status === 409)
        message.error("Ya existe un QR para este cliente.");
    } finally {
      setQr(null);
      setLoading(false);
      update!(true);
    }
  };

  const clearAddress = async () => {
    dispatch(setOrigin(undefined));
    setAddressInputKey((key) => key + 1);
    setQr((prev) =>
      prev
        ? {
            ...prev,
            address: undefined,
            latitude: undefined,
            longitude: undefined,
          }
        : prev,
    );

    if (!qr?.id) return;

    try {
      setLoading(true);

      const patchAdminQr: PatchAdminQrDTO = new PatchAdminQrDTO();
      patchAdminQr.address = null;
      patchAdminQr.longitude = null;
      patchAdminQr.latitude = null;
      patchAdminQr.fee = qr.fee;
      patchAdminQr.countryId = country.value;

      await updateAdminQr(patchAdminQr, qr.id);
      message.success("Dirección eliminada satisfactoriamente.");
      update!(true);
    } catch (error) {
      message.error("Ha ocurrido un error al eliminar la dirección.");
    } finally {
      setLoading(false);
    }
  };

  const qrExpired = useMemo(() => {
    return qr?.expiration && dayjs(qr.expiration).isBefore(dayjs());
  }, [qr?.expiration]);

  const onChangeFee = (fee: number | null) => {
    if (fee) {
      setQr((prev) => {
        return {
          ...prev,
          fee,
        };
      });
    }
  };

  return (
    <Row wrap={false} justify="center">
      <Row wrap={false} gutter={20}>
        <Col>
          {qr?.adminId ? (
            <Space direction="vertical">
              <LabelAndInput label="Código" style={{ width: "100%" }}>
                <InputNumber
                  value={qr?.fee}
                  onChange={onChangeFee}
                  size="middle"
                  addonBefore="%"
                  min={1}
                  style={{ width: "100%" }}
                />
              </LabelAndInput>
              <LabelAndInput
                label={
                  optionalAddressAllowed ? "Origen (opcional): " : "Origen: "
                }
                boldText={qr?.address ?? ""}
                style={{ width: "100%" }}
              >
                <ComponentPlacesAutocomplete
                  key={addressInputKey}
                  type="origin"
                />
              </LabelAndInput>
              {optionalAddressAllowed && (
                <Button
                  danger
                  onClick={clearAddress}
                  loading={loading}
                  style={{ width: "100%" }}
                >
                  Limpiar dirección
                </Button>
              )}
              <Button
                type="primary"
                onClick={() => upsertQr(true)}
                loading={loading}
                style={{ width: "100%" }}
              >
                Editar QR
              </Button>
            </Space>
          ) : (
            <Space direction="vertical">
              <LabelAndInput label="Tamaño" style={{ width: "100%" }}>
                <InputNumber
                  defaultValue={qr?.qrSize}
                  onChange={onChangeSize}
                  size="middle"
                  addonBefore="px"
                  style={{ width: "100%" }}
                />
              </LabelAndInput>

              <LabelAndInput label="Nivel" style={{ width: "100%" }}>
                <Select
                  defaultValue={qr?.errorLevel}
                  size="middle"
                  onChange={onChangeErrorLevel}
                  options={[
                    {
                      value: "L",
                      label: "L",
                    },
                    {
                      value: "M",
                      label: "M",
                    },
                    {
                      value: "Q",
                      label: "Q",
                    },
                    {
                      value: "H",
                      label: "H",
                    },
                  ]}
                  style={{ width: "100%" }}
                />
              </LabelAndInput>

              <LabelAndInput label="Fecha expiración" style={{ width: "100%" }}>
                <DatePicker
                  defaultValue={dayjs(qr?.expiration)}
                  onChange={onChangeExpiration}
                  size="middle"
                  format={"DD-MM-YYYY"}
                  style={{ width: "100%" }}
                />
              </LabelAndInput>

              <LabelAndInput label="Color" style={{ width: "100%" }}>
                <Input
                  type="color"
                  defaultValue={qr?.fgColor}
                  onChange={(e) => onChangeColor(e, "fgColor")}
                  style={{ width: "100%" }}
                />
              </LabelAndInput>
              <LabelAndInput label="Código" style={{ width: "100%" }}>
                <InputNumber
                  defaultValue={qr?.fee}
                  onChange={onChangeFee}
                  size="middle"
                  addonBefore="%"
                  min={1}
                  style={{ width: "100%" }}
                />
              </LabelAndInput>

              <LabelAndInput
                label={optionalAddressAllowed ? "Origen (opcional)" : "Origen"}
                style={{ width: "100%" }}
              >
                <ComponentPlacesAutocomplete
                  key={addressInputKey}
                  type="origin"
                />
              </LabelAndInput>

              {optionalAddressAllowed && (
                <Button
                  danger
                  onClick={clearAddress}
                  loading={loading}
                  style={{ width: "100%" }}
                >
                  Limpiar dirección
                </Button>
              )}

              <Button
                type="primary"
                onClick={() => upsertQr()}
                loading={loading}
                style={{ width: "100%" }}
              >
                Generar QR
              </Button>
            </Space>
          )}
        </Col>
        <Col
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {qr?.adminId && qrExpired ? (
            <Alert
              message="El Código QR ha expirado."
              type="error"
              style={{ width: "100%", marginBottom: 20 }}
            />
          ) : qr?.adminId ? (
            <Alert
              message={`Fecha de expiración ${dayjs(qr?.expiration).format(
                "DD-MM-YYYY HH:mm:ss",
              )}`}
              type="info"
              style={{ marginBottom: 20 }}
            />
          ) : null}

          <QRCodeCanvas
            id="qr-code"
            value={`${qrUrlValue}${qr?.code}`}
            size={qr?.qrSize || defaultQR.qrSize}
            bgColor={qr?.bgColor || defaultQR.bgColor}
            fgColor={qr?.fgColor || defaultQR.fgColor}
            level={qr?.errorLevel}
            includeMargin={false}
            imageSettings={{
              src: mowi,
              x: undefined,
              y: undefined,
              height: qr?.imgQrSize || 32,
              width: qr?.imgQrSize || 32,
              excavate: true,
            }}
            style={{ marginBottom: 20 }}
          />
          {!qr?.adminId && (
            <Alert
              message={
                <div style={{ textAlign: "center" }}>
                  Vista previa del QR
                  <br />
                  <b>No válido para ser usado!</b>
                </div>
              }
              type="warning"
            />
          )}
          {qr?.adminId && !qrExpired && (
            <Button
              type="primary"
              style={{ width: "100%" }}
              onClick={downloadQRCode}
            >
              Descargar
            </Button>
          )}
        </Col>
      </Row>
    </Row>
  );
};

export default AirportQRForm;
